import React, { useState, useEffect, useRef } from 'react';
import { Progress } from '@chakra-ui/react';

/**
 * AnimatedProgress - A Progress bar that smoothly increments to the target value
 * instead of jumping directly to it, similar to LoadingBar.js
 * 
 * @param {number} value - Target progress value (0-100)
 * @param {string} colorScheme - Chakra UI color scheme (e.g., "pink", "blue")
 * @param {string} size - Chakra UI size (e.g., "xs", "sm", "md", "lg")
 * @param {string} width - CSS width value
 * @param {number|string} my - Chakra UI margin-y value
 * @param {function} onProgressChange - Callback that receives the current animated progress value
 * @param {object} ...rest - Any other props to pass to the Progress component
 */
const AnimatedProgress = ({ value = 0, colorScheme = "pink", size = "xs", width = "100%", my = 1, onProgressChange, ...rest }) => {
    const [animatedProgress, setAnimatedProgress] = useState(0);
    const onProgressChangeRef = useRef(onProgressChange);

    // Keep the ref updated with the latest callback
    useEffect(() => {
        onProgressChangeRef.current = onProgressChange;
    }, [onProgressChange]);

    // Call the callback whenever animated progress changes
    useEffect(() => {
        if (onProgressChangeRef.current) {
            onProgressChangeRef.current(animatedProgress);
        }
    }, [animatedProgress]);

    useEffect(() => {
        // Cap the target progress at 99%
        const cappedProgress = Math.min(value, 99);
        
        // Calculate the difference between current animated progress and new target
        const progressDifference = cappedProgress - animatedProgress;
        
        // Only use "minus 6" logic if the jump is significant (more than 6%)
        // Otherwise, animate from current position
        let startProgress;
        if (progressDifference > 6) {
            startProgress = Math.max(0, cappedProgress - 6);
        } else {
            startProgress = animatedProgress;
        }
        
        setAnimatedProgress(startProgress);

        // If we need to animate up to the target progress
        if (startProgress < cappedProgress) {
            let timeoutId;
            
            const animateStep = () => {
                setAnimatedProgress(current => {
                    const next = current + 1;
                    if (next >= cappedProgress) {
                        return cappedProgress;
                    } else {
                        // Schedule next step with random delay between 0.4 and 1.1 seconds
                        const randomDelay = Math.random() * 700 + 400; // 400-1100ms
                        timeoutId = setTimeout(animateStep, randomDelay);
                    }
                    return next;
                });
            };
            
            // Start the first step with initial random delay
            const initialDelay = Math.random() * 700 + 400;
            timeoutId = setTimeout(animateStep, initialDelay);

            // Cleanup function to clear timeout if component unmounts or progress changes
            return () => clearTimeout(timeoutId);
        }
    }, [value]);

    return (
        <Progress
            colorScheme={colorScheme}
            size={size}
            width={width}
            my={my}
            value={animatedProgress}
            {...rest}
        />
    );
};

export default AnimatedProgress;

